import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { concatLatestFrom } from '@ngrx/operators';
import { map, of, switchMap, tap } from 'rxjs';
import { EffectsBase } from '@ps/core-data-access';
import { BuyerService } from '../services/buyer.service';
import { BuyerActions } from './buyer.actions';
import { selectBuyerSettings } from './buyer.selectors';
import { ToastService } from '@ps/core-overlay';

@Injectable()
export class BuyerEffects extends EffectsBase {
  private readonly _actions$ = inject(Actions);
  private readonly _store = inject(Store);
  private readonly _service = inject(BuyerService);
  private readonly _toastService = inject(ToastService);

  public readonly loadBuyerSettings$ = createEffect(() => {
    return this._actions$.pipe(
      ofType(BuyerActions.loadBuyerSettings),
      switchMap(({ buyerId }) =>
        this._service.getByBuyerId(buyerId).pipe(
          map((buyer) => BuyerActions.loadBuyerSettingsSuccess({ buyer })),
          this.psCatchError(
            (error) =>
              of(
                BuyerActions.loadBuyerSettingsFailure({
                  error: error.message,
                }),
              ),
            {
              notify: true,
              customMessage: 'Failed to load buyer settings. Please try again.',
            },
          ),
        ),
      ),
    );
  });

  public readonly loadDecipherHashKeyIds$ = createEffect(() => {
    return this._actions$.pipe(
      ofType(BuyerActions.loadBuyerSettings),
      switchMap(() =>
        this._service.getDecipherHashKeyIds().pipe(
          map((keyIds) =>
            BuyerActions.loadDecipherHashKeyIdsSuccess({ keyIds }),
          ),
          this.psCatchError(
            (error) =>
              of(
                BuyerActions.loadDecipherHashKeyIdsFailure({
                  error: error.message,
                }),
              ),
            { notify: false },
          ),
        ),
      ),
    );
  });

  public readonly saveBuyerSettings$ = createEffect(() => {
    return this._actions$.pipe(
      ofType(BuyerActions.saveBuyerSettings),
      concatLatestFrom(() => this._store.select(selectBuyerSettings)),
      switchMap(([{ cmp }, buyerSettings]) =>
        this._service.update(cmp, buyerSettings).pipe(
          tap(() =>
            this._toastService.success(
              'Success! Buyer information has been added',
            ),
          ),
          map(() => BuyerActions.saveBuyerSettingsSuccess()),
          this.psCatchError(
            (error) =>
              of(
                BuyerActions.saveBuyerSettingsFailure({
                  error: error.message,
                }),
              ),
            {
              notify: true,
              customMessage: 'Failed to save buyer settings. Please try again.',
            },
          ),
        ),
      ),
    );
  });

  public readonly loadBillingRules$ = createEffect(() => {
    return this._actions$.pipe(
      ofType(BuyerActions.loadBillingRules),
      switchMap(() =>
        this._service.getBillingRules().pipe(
          map((billingRules) =>
            BuyerActions.loadBillingRulesSuccess({ billingRules }),
          ),
          this.psCatchError(
            (error) =>
              of(
                BuyerActions.loadBillingRulesFailure({ error: error.message }),
              ),
            { notify: false },
          ),
        ),
      ),
    );
  });

  public readonly createBillingRules$ = createEffect(() => {
    return this._actions$.pipe(
      ofType(BuyerActions.createBillingRules),
      switchMap(({ billingRules }) =>
        this._service.createBillingRules(billingRules).pipe(
          tap(() =>
            this._toastService.success(
              'Success! Billing rules have been created',
            ),
          ),
          map((saved) =>
            BuyerActions.createBillingRulesSuccess({ billingRules: saved }),
          ),
          this.psCatchError(
            (error) =>
              of(
                BuyerActions.createBillingRulesFailure({
                  error: error.message,
                }),
              ),
            {
              notify: true,
              customMessage:
                'Failed to create billing rules. Please try again.',
            },
          ),
        ),
      ),
    );
  });

  public readonly updateBillingRules$ = createEffect(() => {
    return this._actions$.pipe(
      ofType(BuyerActions.updateBillingRules),
      switchMap(({ billingRules }) =>
        this._service.updateBillingRules(billingRules).pipe(
          tap(() =>
            this._toastService.success(
              'Success! Billing rules have been updated',
            ),
          ),
          map((saved) =>
            BuyerActions.updateBillingRulesSuccess({ billingRules: saved }),
          ),
          this.psCatchError(
            (error) =>
              of(
                BuyerActions.updateBillingRulesFailure({
                  error: error.message,
                }),
              ),
            {
              notify: true,
              customMessage:
                'Failed to update billing rules. Please try again.',
            },
          ),
        ),
      ),
    );
  });
}
